// src/components/TableRemitos.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Table,
} from "react-bootstrap";

import {
  getRemitos,
  deleteRemito,
  getRemitoById,
} from "../helpers/rutaRemitos";

import ModalFormRemito from "../components/ModalFormRemito";

import "../css/remitos.css";

const REGISTROS_POR_PAGINA = 10;
const PAGINAS_VISIBLES = 4;

const COLS = {
  acciones: 100,
  nro: 90,
  fecha: 120,
  viaje: 120,
  kg: 110,
  precio: 120,
  persona: 220,
  variedad: 180,
  money: 140,
  dtv: 160,
  lote: 160,
  nota: 260,
  bool: 90,
};

const trunc = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const fmtDate = (d) => {
  if (!d) {
    return "-";
  }

  const date =
    new Date(d);

  const year =
    date.getUTCFullYear();

  const month =
    String(
      date.getUTCMonth() +
        1
    ).padStart(
      2,
      "0"
    );

  const day =
    String(
      date.getUTCDate()
    ).padStart(
      2,
      "0"
    );

  return `${day}/${month}/${year}`;
};

const fmtMoney = (n) =>
  Number(
    n || 0
  ).toLocaleString(
    "es-AR",
    {
      minimumFractionDigits: 2,
    }
  );

const TableRemitos = () => {
  const [
    state,
    setState,
  ] = useState({
    remitos: [],
    loading: true,
    error: "",
  });

  const [
    remitoSel,
    setRemitoSel,
  ] = useState(null);

  const [
    showEdit,
    setShowEdit,
  ] = useState(false);

  const [
    busqueda,
    setBusqueda,
  ] = useState("");

  const [
    pagina,
    setPagina,
  ] = useState(1);

  /* =====================================================
     CARGA
  ===================================================== */

  const load =
    useCallback(
      async () => {
        try {
          const list =
            await getRemitos();

          const arr =
            Array.isArray(list)
              ? list
              : list?.remitos ||
                [];

          arr.sort(
            (a, b) =>
              (
                b.codrem ||
                0
              ) -
                (
                  a.codrem ||
                  0
                ) ||
              new Date(
                b.fecha
              ) -
                new Date(
                  a.fecha
                )
          );

          setState({
            remitos: arr,
            loading: false,
            error: "",
          });
        } catch (e) {
          setState(
            (s) => ({
              ...s,
              loading: false,
              error:
                e?.message ||
                "Error al cargar remitos",
            })
          );
        }
      },
      []
    );

  useEffect(() => {
    load();
  }, [load]);

  /* =====================================================
     EDICIÓN
  ===================================================== */

  const handleCloseEdit =
    useCallback(() => {
      setShowEdit(false);

      setRemitoSel(null);

      setTimeout(
        () => load(),
        0
      );
    }, [load]);

  const onEdit =
    useCallback(
      async (id) => {
        try {
          setRemitoSel(null);

          const resp =
            await getRemitoById(
              id
            );

          if (
            resp?.ok &&
            resp.remito
          ) {
            setRemitoSel(
              resp.remito
            );

            setShowEdit(true);
          } else {
            console.error(
              "❌ getRemitoById:",
              resp?.error ||
                resp
            );

            alert(
              "No se pudo cargar el remito."
            );
          }
        } catch (e) {
          console.error(e);

          alert(
            "Error de red al cargar el remito."
          );
        }
      },
      []
    );

  const onDelete =
    useCallback(
      async (id) => {
        if (
          !window.confirm(
            "¿Borrar (lógico) este remito?"
          )
        ) {
          return;
        }

        try {
          await deleteRemito(
            id
          );

          load();
        } catch (e) {
          console.error(e);
        }
      },
      [load]
    );

  /* =====================================================
     BÚSQUEDA
  ===================================================== */

  const remitosFiltrados =
    useMemo(() => {
      const texto =
        busqueda
          .trim()
          .toLowerCase();

      if (!texto) {
        return state.remitos;
      }

      return state.remitos.filter(
        (r) => {
          const valores = [
            r.codrem,
            r.nroviaje,
            r.nroViaje,
            r.proveedor
              ?.razonsocial,
            r.cliente
              ?.razonsocial,
            r.transporte
              ?.transporte,
            r.chofer
              ?.nombre,
            r.variedad,
            r.lote?.lote,
          ];

          return valores.some(
            (valor) =>
              String(
                valor || ""
              )
                .toLowerCase()
                .includes(
                  texto
                )
          );
        }
      );
    }, [
      state.remitos,
      busqueda,
    ]);

  useEffect(() => {
    setPagina(1);
  }, [busqueda]);

  /* =====================================================
     PAGINACIÓN
  ===================================================== */

  const totalPaginas =
    Math.max(
      1,
      Math.ceil(
        remitosFiltrados.length /
          REGISTROS_POR_PAGINA
      )
    );

  useEffect(() => {
    if (
      pagina >
      totalPaginas
    ) {
      setPagina(
        totalPaginas
      );
    }
  }, [
    pagina,
    totalPaginas,
  ]);

  const inicio =
    (
      pagina -
      1
    ) *
    REGISTROS_POR_PAGINA;

  const fin =
    inicio +
    REGISTROS_POR_PAGINA;

  const remitosPagina =
    remitosFiltrados.slice(
      inicio,
      fin
    );

  const paginas =
    useMemo(() => {
      let primera =
        pagina - 1;

      if (
        primera <
        1
      ) {
        primera = 1;
      }

      let ultima =
        primera +
        PAGINAS_VISIBLES -
        1;

      if (
        ultima >
        totalPaginas
      ) {
        ultima =
          totalPaginas;

        primera =
          Math.max(
            1,
            ultima -
              PAGINAS_VISIBLES +
              1
          );
      }

      const resultado =
        [];

      for (
        let i = primera;
        i <= ultima;
        i++
      ) {
        resultado.push(i);
      }

      return resultado;
    }, [
      pagina,
      totalPaginas,
    ]);

  /* =====================================================
     ESTADOS
  ===================================================== */

  if (
    state.loading
  ) {
    return (
      <div className="rem2-loading">
        Cargando remitos...
      </div>
    );
  }

  if (
    state.error
  ) {
    return (
      <div className="p-3 text-danger">
        {state.error}
      </div>
    );
  }

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="rem2-card">

      {/* TOOLBAR */}

      <div className="rem2-toolbar">
        <div className="rem2-toolbar-title">
          <h4>
            Remitos registrados
          </h4>

          <span>
            {
              remitosFiltrados.length
            }{" "}
            registros
          </span>
        </div>

        <div className="rem2-search">
          <label>
            Buscar
          </label>

          <input
            type="text"
            className="form-control"
            placeholder="Remito, viaje, proveedor..."
            value={busqueda}
            onChange={(
              e
            ) =>
              setBusqueda(
                e.target.value
              )
            }
          />

          {busqueda && (
            <button
              type="button"
              className="rem2-clear"
              onClick={() =>
                setBusqueda("")
              }
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* INFO */}

      <div className="rem2-info">
        <span>
          Mostrando{" "}
          <strong>
            {remitosFiltrados.length
              ? inicio + 1
              : 0}
          </strong>{" "}
          a{" "}
          <strong>
            {Math.min(
              fin,
              remitosFiltrados.length
            )}
          </strong>{" "}
          de{" "}
          <strong>
            {
              remitosFiltrados.length
            }
          </strong>
        </span>

        <span>
          Página{" "}
          <strong>
            {pagina}
          </strong>{" "}
          de{" "}
          <strong>
            {
              totalPaginas
            }
          </strong>
        </span>
      </div>

      {/* TABLA */}

      <div className="rem2-table-scroll">
        <Table
          striped
          bordered
          hover
          className="rem2-table"
          style={{
            tableLayout:
              "fixed",
            width: "100%",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width:
                    COLS.acciones,
                }}
              >
                Acciones
              </th>

              <th
                style={{
                  width:
                    COLS.nro,
                }}
              >
                Remito
              </th>

              <th
                style={{
                  width:
                    COLS.fecha,
                }}
              >
                Fecha
              </th>

              <th
                style={{
                  width:
                    COLS.viaje,
                }}
              >
                Nro Viaje
              </th>

              <th
                style={{
                  width:
                    COLS.persona,
                }}
              >
                Proveedor
              </th>

              <th
                style={{
                  width:
                    COLS.persona,
                }}
              >
                Cliente
              </th>

              <th
                style={{
                  width:
                    COLS.kg,
                }}
              >
                KG Campo
              </th>

              <th
                style={{
                  width:
                    COLS.kg,
                }}
              >
                KG Recibidos
              </th>

              <th
                style={{
                  width:
                    COLS.kg,
                }}
              >
                Diferencia
              </th>

              <th
                style={{
                  width:
                    COLS.precio,
                }}
              >
                Precio Compra
              </th>

              <th
                style={{
                  width:
                    COLS.precio,
                }}
              >
                Precio Venta
              </th>

              <th
                style={{
                  width:
                    COLS.persona,
                }}
              >
                Transporte
              </th>

              <th
                style={{
                  width:
                    COLS.persona,
                }}
              >
                Chofer
              </th>

              <th
                style={{
                  width:
                    COLS.lote,
                }}
              >
                Lote
              </th>

              <th
                style={{
                  width:
                    COLS.variedad,
                }}
              >
                Variedad
              </th>

              <th
                style={{
                  width:
                    COLS.money,
                }}
              >
                Total Compra
              </th>

              <th
                style={{
                  width:
                    COLS.money,
                }}
              >
                Total Venta
              </th>

              <th
                style={{
                  width:
                    COLS.money,
                }}
              >
                Comisión
              </th>

              <th
                style={{
                  width:
                    COLS.dtv,
                }}
              >
                Nro DTV Compra
              </th>

              <th
                style={{
                  width:
                    COLS.dtv,
                }}
              >
                Nro DTV Venta
              </th>

              <th
                style={{
                  width:
                    COLS.bool,
                  textAlign:
                    "center",
                }}
              >
                Factura
              </th>

              <th
                style={{
                  width:
                    COLS.nota,
                }}
              >
                Notas
              </th>
            </tr>
          </thead>

          <tbody>
            {remitosPagina.length ===
            0 ? (
              <tr>
                <td
                  colSpan={22}
                  className="rem2-empty"
                >
                  No hay remitos.
                </td>
              </tr>
            ) : (
              remitosPagina.map(
                (r) => (
                  <tr
                    key={
                      r._id
                    }
                  >
                    <td
                      style={{
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      <div className="rem2-row-actions">
                        <button
                          className="btn btn-primary"
                          title="Editar"
                          onClick={() =>
                            onEdit(
                              r._id
                            )
                          }
                        >
                          <i className="fa fa-pencil-square-o" />
                        </button>
                      </div>
                    </td>

                    <td>
                      {
                        r.codrem
                      }
                    </td>

                    <td>
                      {fmtDate(
                        r.fecha
                      )}
                    </td>

                    <td
                      style={
                        trunc
                      }
                    >
                      {r.nroviaje ||
                        r.nroViaje ||
                        "-"}
                    </td>

                    <td
                      style={
                        trunc
                      }
                    >
                      {r.proveedor
                        ?.razonsocial ||
                        "-"}
                    </td>

                    <td
                      style={
                        trunc
                      }
                    >
                      {r.cliente
                        ?.razonsocial ||
                        "-"}
                    </td>

                    <td>
                      {
                        r.kgCampo
                      }
                    </td>

                    <td>
                      {
                        r.kgRecibido
                      }
                    </td>

                    <td>
                      {
                        r.diferencia
                      }
                    </td>

                    <td>
                      {fmtMoney(
                        r.precioCompra
                      )}
                    </td>

                    <td>
                      {fmtMoney(
                        r.precioVenta
                      )}
                    </td>

                    <td
                      style={
                        trunc
                      }
                    >
                      {r.transporte
                        ?.transporte ||
                        "-"}
                    </td>

                    <td
                      style={
                        trunc
                      }
                    >
                      {r.chofer
                        ?.nombre ||
                        "-"}
                    </td>

                    <td
                      style={{
                        ...trunc,
                        textAlign:
                          "left",
                      }}
                    >
                      {r.lote
                        ? r.lote
                            .codlote !=
                          null
                          ? `#${r.lote.codlote} - ${r.lote.lote}`
                          : r.lote
                              .lote
                        : "—"}
                    </td>

                    <td
                      style={
                        trunc
                      }
                    >
                      {r.variedad ||
                        "-"}
                    </td>

                    <td>
                      {fmtMoney(
                        r.totalCompra
                      )}
                    </td>

                    <td>
                      {fmtMoney(
                        r.totalVenta
                      )}
                    </td>

                    <td>
                      {fmtMoney(
                        r.comision
                      )}
                    </td>

                    <td
                      style={
                        trunc
                      }
                    >
                      {r.nroDTVCompra ||
                        "-"}
                    </td>

                    <td
                      style={
                        trunc
                      }
                    >
                      {r.nroDTVVenta ||
                        "-"}
                    </td>

                    <td
                      style={{
                        width:
                          COLS.bool,
                        textAlign:
                          "center",
                      }}
                    >
                      {r.factura
                        ? "✅"
                        : "—"}
                    </td>

                    <td
                      style={
                        trunc
                      }
                    >
                      {r.nota ||
                        "-"}
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </Table>
      </div>

      {/* PAGINACIÓN */}

      <div className="rem2-pagination">
        <span className="rem2-page-size">
          10 remitos por página
        </span>

        <div className="rem2-pagination-buttons">
          <button
            type="button"
            disabled={
              pagina === 1
            }
            onClick={() =>
              setPagina(1)
            }
          >
            «
          </button>

          <button
            type="button"
            disabled={
              pagina === 1
            }
            onClick={() =>
              setPagina(
                pagina - 1
              )
            }
          >
            ‹
          </button>

          {paginas.map(
            (numero) => (
              <button
                type="button"
                key={numero}
                className={
                  numero ===
                  pagina
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setPagina(
                    numero
                  )
                }
              >
                {numero}
              </button>
            )
          )}

          <button
            type="button"
            disabled={
              pagina ===
              totalPaginas
            }
            onClick={() =>
              setPagina(
                pagina + 1
              )
            }
          >
            ›
          </button>

          <button
            type="button"
            disabled={
              pagina ===
              totalPaginas
            }
            onClick={() =>
              setPagina(
                totalPaginas
              )
            }
          >
            »
          </button>
        </div>
      </div>

      <ModalFormRemito
        show={showEdit}
        remito={remitoSel}
        onClose={
          handleCloseEdit
        }
      />
    </div>
  );
};

export default TableRemitos;